(() => {
  if (customElements.get('bm-product-gallery')) return;

  class BMProductGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.slides = [...this.querySelectorAll('[data-bm-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-bm-gallery-thumb]')];
      this.viewport = this.querySelector('[data-bm-gallery-viewport]');
      this.counter = this.querySelector('[data-bm-gallery-counter]');
      this.dialog = this.querySelector('[data-bm-gallery-dialog]');
      this.dialogImage = this.querySelector('[data-bm-dialog-image]');
      this.dialogCount = this.querySelector('[data-bm-dialog-count]');
      this.lightboxEnabled = this.dataset.lightbox === 'true';
      this.touchStartX = null;
      this.touchStartY = null;
      this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));

      if (!this.slides.length) return;
      this.bindEvents();
      this.goTo(this.index, false);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    bindEvents() {
      const options = { signal: this.signal };

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-bm-gallery-thumb]');
        if (thumb) {
          this.goTo(Number(thumb.dataset.index));
          return;
        }

        if (event.target.closest('[data-bm-gallery-prev]')) {
          this.step(-1);
          return;
        }

        if (event.target.closest('[data-bm-gallery-next]')) {
          this.step(1);
          return;
        }

        if (event.target.closest('[data-bm-gallery-open]') && this.lightboxEnabled) {
          this.openDialog();
        }
      }, options);

      this.addEventListener('keydown', (event) => {
        if (this.dialog?.open) return;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.goTo(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          this.goTo(this.slides.length - 1);
        }
      }, options);

      this.viewport?.addEventListener('touchstart', (event) => {
        const touch = event.touches?.[0];
        if (!touch) return;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }, { passive: true, signal: this.signal });

      this.viewport?.addEventListener('touchend', (event) => {
        const touch = event.changedTouches?.[0];
        if (!touch || this.touchStartX === null || this.touchStartY === null) return;
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        this.touchStartX = null;
        this.touchStartY = null;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.step(deltaX < 0 ? 1 : -1);
      }, { passive: true, signal: this.signal });

      if (!this.dialog) return;

      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-bm-dialog-close]')) {
          this.closeDialog();
          return;
        }
        if (event.target.closest('[data-bm-dialog-prev]')) {
          this.step(-1);
          this.syncDialogImage();
          return;
        }
        if (event.target.closest('[data-bm-dialog-next]')) {
          this.step(1);
          this.syncDialogImage();
        }
      }, options);

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
          this.syncDialogImage();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
          this.syncDialogImage();
        }
      }, options);

      this.dialog.addEventListener('close', () => this.afterDialogClose(), options);
    }

    goTo(index, scrollThumb = true) {
      if (!this.slides.length) return;
      const normalized = (index + this.slides.length) % this.slides.length;
      this.index = normalized;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === normalized;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === normalized;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      if (this.counter) this.counter.textContent = `${normalized + 1} / ${this.slides.length}`;
      if (scrollThumb) this.thumbs[normalized]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      this.preloadAdjacent();
    }

    step(direction) {
      this.goTo(this.index + direction);
    }

    preloadAdjacent() {
      if (this.slides.length < 2) return;
      [-1, 1].forEach((offset) => {
        const index = (this.index + offset + this.slides.length) % this.slides.length;
        const image = this.slides[index]?.querySelector('img');
        if (image) image.loading = 'eager';
      });
    }

    openDialog() {
      if (!this.dialog || !this.dialogImage) return;
      this.syncDialogImage();
      document.documentElement.classList.add('bm-gallery-dialog-open');
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.dispatchEvent(new CustomEvent('product:modal-open'));
      requestAnimationFrame(() => this.querySelector('[data-bm-dialog-close]')?.focus());
    }

    closeDialog() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.afterDialogClose();
      }
    }

    afterDialogClose() {
      document.documentElement.classList.remove('bm-gallery-dialog-open');
      document.dispatchEvent(new CustomEvent('product:modal-close'));
      this.slides[this.index]?.querySelector('[data-bm-gallery-open]')?.focus({ preventScroll: true });
    }

    syncDialogImage() {
      if (!this.dialogImage) return;
      const slide = this.slides[this.index];
      if (!slide) return;
      const src = slide.dataset.fullSrc;
      const srcset = slide.dataset.fullSrcset;
      if (src) this.dialogImage.src = src;
      if (srcset) this.dialogImage.srcset = srcset;
      else this.dialogImage.removeAttribute('srcset');
      this.dialogImage.sizes = '96vw';
      this.dialogImage.alt = slide.dataset.alt || '';
      if (this.dialogCount) this.dialogCount.textContent = `${this.index + 1} / ${this.slides.length}`;
    }
  }

  customElements.define('bm-product-gallery', BMProductGallery);
})();
